<script setup lang="ts">
import { ChipComponent } from '@metapic-apps/shared'
import { ref, watch } from 'vue'

import { isValidEmail, isValidIdentifier } from '@/lib/helpers/utils.helpers'
import { type CampaignFormType, PreregisterItemEnum, type PreregisterItemType } from '@/lib/types/campaign.types'
import { BoxComponent, FlexComponent, InputComponent, TypographyComponent } from '@/ui-library'

const tabs = [
  {
    key: PreregisterItemEnum.Email,
    title: 'Email',
  },
  {
    key: PreregisterItemEnum.SocialMediaUser,
    title: 'Instagram',
  },
]
const placeholder = {
  [PreregisterItemEnum.Email]: 'Enter email',
  [PreregisterItemEnum.SocialMediaUser]: 'Enter instagram',
}
const activeTab = ref(tabs[0]!.key)
const inputValue = ref('')
const preregisterItems = defineModel<CampaignFormType['preregisterItems']>('modelValue', { required: true })
const emit = defineEmits(['update:modelValue'])
const handleClose = (tag: PreregisterItemType) =>
  emit(
    'update:modelValue',
    preregisterItems.value.filter((item) => item.identifier !== tag.identifier),
  )

const isDuplicate = (newItem: string) => {
  return !!preregisterItems.value.find((item) => item.identifier === newItem)
}
const handleChange = (value: string) => {
  const isMultipleEntities = value.split(',').length > 1
  if (isMultipleEntities) {
    const usersToAdd: PreregisterItemType[] = value
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item && !isDuplicate(item))
      .map((item) => ({ identifier: item, type: activeTab.value }))

    emit('update:modelValue', [...preregisterItems.value, ...usersToAdd])
    inputValue.value = ''
  } else {
    if (value) {
      if (value.endsWith(' ')) {
        const cleanedValue = value.trim()

        if (!isDuplicate(cleanedValue)) {
          const userToAdd: PreregisterItemType = { identifier: cleanedValue, type: activeTab.value }
          emit('update:modelValue', [...preregisterItems.value, userToAdd])
          inputValue.value = ''
        }
      }
    }
  }
}
const handleEnter = (value: string) => {
  const cleanedValue = value.trim()

  if (cleanedValue && !isDuplicate(cleanedValue)) {
    const userToAdd: PreregisterItemType = { identifier: cleanedValue, type: activeTab.value }
    emit('update:modelValue', [...preregisterItems.value, userToAdd])
    inputValue.value = ''
  }
}
const isItemValid = (item: PreregisterItemType) => {
  if (item.type === PreregisterItemEnum.SocialMediaUser) {
    return isValidIdentifier(item.identifier)
  }
  return isValidEmail(item.identifier)
}

watch(
  () => activeTab.value,
  () => (inputValue.value = ''),
)
</script>
<template>
  <div>
    <FlexComponent class="campaign-form-preregister-users-input__tabs">
      <BoxComponent
        v-for="item in tabs"
        :key="item.key"
        :class="{
          'campaign-form-preregister-users-input__tab_active': activeTab === item.key,
        }"
        :pt="8"
        :pb="8"
        :pr="16"
        :pl="16"
        class="campaign-form-preregister-users-input__tab"
        @click="activeTab = item.key"
      >
        <TypographyComponent variant="label-large">
          {{ item.title }}
        </TypographyComponent>
      </BoxComponent>
    </FlexComponent>
    <InputComponent
      v-model="inputValue"
      size="large"
      :placeholder="placeholder[activeTab]"
      :class="{ 'campaign-form-preregister-users-input__tabs-dirty': preregisterItems.length > 0 }"
      @change="handleEnter"
      @input="handleChange"
    />
    <FlexComponent v-if="preregisterItems.length > 0" wrap="wrap" :gap="8" class="campaign-form-preregister-users-input__values-container">
      <ChipComponent
        v-for="item in preregisterItems"
        :key="item.identifier"
        :variant="isItemValid(item) ? 'default' : 'danger'"
        closable
        @close="handleClose(item)"
      >
        {{ item.label || item.identifier }}
      </ChipComponent>
    </FlexComponent>
  </div>
</template>
<style lang="scss" scoped>
:deep(.el-input) {
  z-index: 1;
}
:deep(.el-input__wrapper) {
  border-top-left-radius: 0;
}
:deep(.campaign-form-preregister-users-input__tabs-dirty) {
  .el-input__wrapper {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
}
.campaign-form-preregister-users-input__values-container {
  position: relative;
  padding: 8px;
  border-radius: 0 0 8px 8px;
  background: var(--inverted);
  border: none;
  box-shadow: 0 0 0 1px var(--secondary-12);
}
.campaign-form-preregister-users-input__tabs {
  bottom: -1px;
  background: var(--inverted);
}
.campaign-form-preregister-users-input__tab {
  cursor: pointer;
  border: none;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}
.campaign-form-preregister-users-input__tab_active {
  box-shadow: 0 0 0 1px var(--secondary-12);
}
</style>
